import React from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../../core-components/theme/components/Buttons';
import type { AlertChannel, PasscodeChannel } from '../types/domain.types';

import { firstChannelLabel, otherChannelLabel } from './identifierText';

export interface SendState {
  readonly sendingChannel: PasscodeChannel | null;
  readonly sending: boolean;
  readonly unavailable: boolean;
  readonly describedBy?: string;
}

const heldBy = ({ sending, unavailable }: SendState, busy: boolean): boolean => unavailable || (sending && !busy);

export interface PrimarySendButtonProps extends SendState {
  readonly channel: AlertChannel | undefined;
  readonly autoFocus?: boolean;
}

export const PrimarySendButton: React.FC<PrimarySendButtonProps> = ({ channel, autoFocus, describedBy, ...state }) => {
  const { t } = useTranslation();
  const busy = channel === undefined ? state.sending : state.sendingChannel === channel;
  return (
    <Button
      type="submit"
      intent="primary"
      busy={busy}
      autoFocus={autoFocus}
      aria-describedby={describedBy}
      aria-disabled={heldBy(state, busy) || undefined}
    >
      {channel === undefined ? t('passcodeAuth.login.sendCode', 'Send code') : firstChannelLabel(t, channel)}
    </Button>
  );
};

export interface OtherChannelSendButtonProps extends SendState {
  readonly channel: AlertChannel;
  readonly onSend: (channel: AlertChannel) => void;
}

export const OtherChannelSendButton: React.FC<OtherChannelSendButtonProps> = ({ channel, onSend, describedBy, ...state }) => {
  const { t } = useTranslation();
  const busy = state.sendingChannel === channel;
  return (
    <Button
      type="button"
      intent="secondary"
      busy={busy}
      aria-describedby={describedBy}
      aria-disabled={heldBy(state, busy) || undefined}
      onClick={() => onSend(channel)}
    >
      {otherChannelLabel(t, channel)}
    </Button>
  );
};
